import { storageErrorMessage } from './session-snapshot.js';

/**
 * Account and favorite guards also cover late downloads and database writes.
 * `status.sensitive` (declared by the caller beside `saved` and `errors`)
 * holds the server's label on each retained clip, saved in its metadata, so a
 * labelled video stays covered after a reload.
 */
export function createFavoriteVideoStorage({ urls, status, getAccountId, isFavorite,
  load, save, remove, fetchBlob, createUrl = URL.createObjectURL, revokeUrl = URL.revokeObjectURL }) {
  let epoch = 0;
  const revisions = new Map();
  const ownedUrls = new Map();
  const pendingMetadata = new Map();
  const current = (accountId, token) => epoch === token && getAccountId() === accountId;
  const release = (id) => { if (ownedUrls.has(id)) revokeUrl(ownedUrls.get(id)); ownedUrls.delete(id); };
  function clear() {
    epoch += 1;
    for (const id of ownedUrls.keys()) release(id);
    for (const id of Object.keys(urls)) delete urls[id];
    status.saved = {};
    status.errors = {};
    status.sensitive = {};
    revisions.clear();
    pendingMetadata.clear();
  }
  return {
    clear,
    async hydrate(accountId) {
      clear();
      const token = epoch;
      const records = await load(accountId);
      if (!current(accountId, token)) return;
      for (const record of records) {
        if (!isFavorite(record.favoriteId)) continue;
        const url = createUrl(record.blob);
        urls[record.favoriteId] = url;
        ownedUrls.set(record.favoriteId, url);
        status.saved[record.favoriteId] = true;
        if (record.metadata?.sensitive === true) status.sensitive[record.favoriteId] = true;
      }
    },
    async save(favoriteId, url, metadata = {}) {
      const accountId = metadata.accountId || getAccountId();
      const token = epoch;
      const revision = (revisions.get(favoriteId) || 0) + 1;
      revisions.set(favoriteId, revision);
      const stillCurrent = () => current(accountId, token) && isFavorite(favoriteId) && revisions.get(favoriteId) === revision;
      if (!accountId || !stillCurrent()) return false;
      metadata = { ...pendingMetadata.get(favoriteId), ...metadata, accountId };
      pendingMetadata.set(favoriteId, metadata);
      release(favoriteId);
      urls[favoriteId] = url;
      status.saved[favoriteId] = false;
      delete status.errors[favoriteId];
      if (metadata.sensitive === true) status.sensitive[favoriteId] = true;
      else delete status.sensitive[favoriteId];
      try {
        const blob = await fetchBlob(url);
        if (!stillCurrent()) return false;
        const saved = await save({ accountId, favoriteId, blob, savedAt: Date.now(), metadata });
        if (!stillCurrent() || !saved) return false;
        const localUrl = createUrl(blob);
        ownedUrls.set(favoriteId, localUrl);
        urls[favoriteId] = localUrl;
        status.saved[favoriteId] = true;
        pendingMetadata.delete(favoriteId);
        return true;
      } catch (error) {
        if (stillCurrent()) status.errors[favoriteId] = storageErrorMessage(error);
        return false;
      }
    },
    async remove(favoriteId) {
      const accountId = getAccountId();
      const token = epoch;
      const revision = (revisions.get(favoriteId) || 0) + 1;
      revisions.set(favoriteId, revision);
      const previousUrl = urls[favoriteId];
      const wasSaved = status.saved[favoriteId];
      const wasSensitive = status.sensitive[favoriteId];
      const previousMetadata = pendingMetadata.get(favoriteId);
      delete urls[favoriteId];
      delete status.saved[favoriteId];
      delete status.errors[favoriteId];
      delete status.sensitive[favoriteId];
      pendingMetadata.delete(favoriteId);
      try {
        if (accountId) await remove(accountId, favoriteId);
        if (current(accountId, token) && revisions.get(favoriteId) === revision) release(favoriteId);
        return true;
      } catch (error) {
        if (current(accountId, token) && isFavorite(favoriteId) && revisions.get(favoriteId) === revision) {
          urls[favoriteId] = previousUrl;
          status.saved[favoriteId] = wasSaved;
          if (wasSensitive) status.sensitive[favoriteId] = true;
          if (previousMetadata) pendingMetadata.set(favoriteId, previousMetadata);
          status.errors[favoriteId] = storageErrorMessage(error);
        }
        return false;
      }
    },
    prune(validIds) {
      // Favorite removal optimistically deletes its public URL before this cleanup.
      for (const id of new Set([...Object.keys(urls), ...ownedUrls.keys(), ...pendingMetadata.keys()])) {
        if (!validIds.has(id)) {
          revisions.set(id, (revisions.get(id) || 0) + 1);
          release(id);
          pendingMetadata.delete(id);
          delete urls[id]; delete status.saved[id]; delete status.errors[id]; delete status.sensitive[id];
        }
      }
    }
  };
}
